import { getAuthToken } from "@asayinc/component-library";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppContainer } from "./components/containers/AppContainer/AppContainer";
import { ProtectedRoute } from "./components/containers/ProtectedRoute";
import { Dashboard } from "./pages/Dashboard";

import { Login } from "./pages/Login";

import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { HomePage } from "./pages/HomePage";
import { Issuers } from "./pages/Issuers";
import { ProxyEvents } from "./pages/ProxyEvents";
import { BankruptcyEvents } from "./pages/BankruptcyEvents";
import { ClassActionEvents } from "./pages/ClassActionEvents";
import { Reports } from "./pages/Reports";
import { NoboEvents } from "./pages/NoboEvents";
import { ShareRangeAnalysisEvents } from "./pages/Reports/ShareRangeAnalysisEvents";
import { VotingProxyEvents } from "./pages/ProxyEvents/VotingProxyEvents";
import { InformationalProxyEvents } from "./pages/ProxyEvents/InformationalProxyEvents";
import { LocationAnalysisEvents } from "./pages/Reports/LocationAnalysisEvents";
import { ShareRangeAnalysisEvent } from "./pages/ShareRangeAnalysisEvent";
import { ShareRangeAnalysisEventDetailsTab } from "./pages/ShareRangeAnalysisEvent/ShareRangeAnalysisEventDetailsTab";
import { ShareRangeAnalysisEventReportsTab } from "./pages/ShareRangeAnalysisEvent/ShareRangeAnalysisEventReportsTab";
import { ShareRangeAnalysisEventStatsTab } from "./pages/ShareRangeAnalysisEvent/ShareRangeAnalysisEventStatsTab";

import { ProxyEventDetailsTab } from "./pages/ProxyEvent/ProxyEventDetailsTab";
import { Issuer } from "./pages/Issuer";
import { IssuerProxyEvents } from "./pages/Issuer/IssuerProxyEvents";
import { IssuerBankruptcyEvents } from "./pages/Issuer/IssuerBankruptyEvents";
import { IssuerClassActionEvents } from "./pages/Issuer/IssuerClassActionEvents";
import { IssuerNoboEvents } from "./pages/Issuer/IssuerNoboEvents";
import { IssuerAdditionalMailings } from "./pages/Issuer/IssuerAdditionalMailings";
import { IssuerReports } from "./pages/Issuer/IssuerReports";
import { IssuerLocationAnalysisEvents } from "./pages/Issuer/IssuerReports/IssuerLocationAnalysisEvents";
import { IssuerShareRangeAnalysisEvents } from "./pages/Issuer/IssuerReports/IssuerShareRangeAnalysisEvents";
import { ProxyEvent } from "./pages/ProxyEvent";
import { ProxyEventMaterialsTab } from "./pages/ProxyEvent/ProxyEventMaterialsTab";
import { ProxyEventVotingTab } from "./pages/ProxyEvent/ProxyEventVotingTab";
import { ProxyEventStatsTab } from "./pages/ProxyEvent/ProxyEventStatsTab";
import { LocationAnalysisEvent } from "./pages/LocationAnalysisEvent";
import { LocationAnalysisEventDetailsTab } from "./pages/LocationAnalysisEvent/LocationAnalysisEventDetailsTab";
import { LocationAnalysisEventReportsTab } from "./pages/LocationAnalysisEvent/LocationAnalysisEventReportsTab";
import { LocationAnalysisEventStatsTab } from "./pages/LocationAnalysisEvent/LocationAnalysisEventStatsTab";
import { NoboEvent } from "./pages/NoboEvent";
import { NoboEventDetailsTab } from "./pages/NoboEvent/NoboEventDetailsTab";
import { NoboEventStatsTab } from "./pages/NoboEvent/NoboEventStatsTab";
import { BankruptcyEvent } from "./pages/BankruptcyEvent";
import { BankruptcyEventDetailsTab } from "./pages/BankruptcyEvent/BankruptcyEventDetailsTab";
import { BankruptcyEventMaterialsTab } from "./pages/BankruptcyEvent/BankruptcyEventMaterialsTab";
import { BankruptcyEventStatsTab } from "./pages/BankruptcyEvent/BankruptcyEventStatsTab";
import { ClassActionEvent } from "./pages/ClassActionEvent";
import { ClassActionEventDetailsTab } from "./pages/ClassActionEvent/ClassActionEventDetailsTab";
import { ClassActionEventMaterialsTab } from "./pages/ClassActionEvent/ClassActionEventMaterialsTab";
import { ClassActionEventStatsTab } from "./pages/ClassActionEvent/ClassActionEventStatsTab";
import { ProxyEventTabulationReportsTab } from "./pages/ProxyEvent/ProxyEventTabulationReportsTab";
import { ProxyEventAdditionalMailingsTab } from "./pages/ProxyEvent/ProxyEventAdditionalMailingsTab";
import { ProxyEventAdditionalMailingRequestsTab } from "./pages/ProxyEvent/ProxyEventAdditionalMailingRequestsTab";
// import { ProxyEventChangeRequestTab } from "./pages/ProxyEvent/ProxyEventChangeRequestTab";
// import { ProxyEventChangeRequests } from "./pages/ProxyEvents/ProxyEventChangeRequests";
import { VotingInstruction } from "./pages/VotingInstruction";
import { Settings } from "./pages/Settings";
import { AdditionalMailings } from "./pages/AdditionalMailings";
import { AdditionalMailing } from "./pages/AdditionalMailing";
import { AdditionalMailingDetailsTab } from "./pages/AdditionalMailing/AdditionalMailingDetailsTab";
import { AdditionalMailingMaterialsTab } from "./pages/AdditionalMailing/AdditionalMailingMaterialsTab";
import { AdditionalMailingStatsTab } from "./pages/AdditionalMailing/AdditionalMailingStatsTab";
import { PageNotFound } from "./pages/PageNotFound";

export function App() {
  const isNotLoggedIn = !getAuthToken();
  const { pathname } = useLocation();

  // Navigate user to login page if they dont have an auth token
  if (
    isNotLoggedIn &&
    pathname !== "/login" &&
    pathname !== "/forgot-password" &&
    pathname !== "/reset-password"
  ) {
    return <Navigate replace to={{ pathname: "/login" }} />;
  }

  return (
    <Routes>
      <Route path="/" element={<AppContainer />}>
        <Route index element={<HomePage />} />
        <Route path="login" element={<Login />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="issuers" element={<Issuers />} />
        {/* Proxy Events - accessible by tabulator and solicitor */}
        <Route
          path="proxy-events"
          element={
            <ProtectedRoute allowedRoles={["tabulator", "solicitor"]}>
              <ProxyEvents />
            </ProtectedRoute>
          }
        >
          <Route path="voting" element={<VotingProxyEvents />} />
          <Route path="informational" element={<InformationalProxyEvents />} />
          {/* <Route path="change-requests" element={<ProxyEventChangeRequests />} /> */}
        </Route>
        <Route
          path="proxy-events/:id"
          element={
            <ProtectedRoute allowedRoles={["tabulator", "solicitor"]}>
              <ProxyEvent />
            </ProtectedRoute>
          }
        >
          <Route path="details" element={<ProxyEventDetailsTab />} />
          <Route path="materials" element={<ProxyEventMaterialsTab />} />
          <Route path="voting" element={<ProxyEventVotingTab />} />
          <Route path="stats" element={<ProxyEventStatsTab />} />
          <Route
            path="additional-mailings"
            element={<ProxyEventAdditionalMailingsTab />}
          />
          <Route
            path="additional-mailing-requests"
            element={<ProxyEventAdditionalMailingRequestsTab />}
          />
          {/* <Route path="change-request" element={<ProxyEventChangeRequestTab />} /> */}
          <Route
            path="tabulation-reports"
            element={<ProxyEventTabulationReportsTab />}
          />
        </Route>
        {/* Additional Mailings - accessible by tabulator and solicitor */}
        <Route
          path="additional-mailings"
          element={
            <ProtectedRoute allowedRoles={["tabulator", "solicitor"]}>
              <AdditionalMailings />
            </ProtectedRoute>
          }
        />
        <Route
          path="additional-mailings/:id"
          element={
            <ProtectedRoute allowedRoles={["tabulator", "solicitor"]}>
              <AdditionalMailing />
            </ProtectedRoute>
          }
        >
          <Route path="details" element={<AdditionalMailingDetailsTab />} />
          <Route path="materials" element={<AdditionalMailingMaterialsTab />} />
          <Route path="stats" element={<AdditionalMailingStatsTab />} />
        </Route>
        {/* Bankruptcy Events - accessible by claims administrator only */}
        <Route
          path="bankruptcy-events"
          element={
            <ProtectedRoute allowedRoles={["claimsAdministrator"]}>
              <BankruptcyEvents />
            </ProtectedRoute>
          }
        />
        <Route
          path="bankruptcy-events/:id"
          element={
            <ProtectedRoute allowedRoles={["claimsAdministrator"]}>
              <BankruptcyEvent />
            </ProtectedRoute>
          }
        >
          <Route path="details" element={<BankruptcyEventDetailsTab />} />
          <Route path="materials" element={<BankruptcyEventMaterialsTab />} />
          <Route path="stats" element={<BankruptcyEventStatsTab />} />
        </Route>
        {/* Class Action Events - accessible by claims administrator only */}
        <Route
          path="class-action-events"
          element={
            <ProtectedRoute allowedRoles={["claimsAdministrator"]}>
              <ClassActionEvents />
            </ProtectedRoute>
          }
        />
        <Route
          path="class-action-events/:id"
          element={
            <ProtectedRoute allowedRoles={["claimsAdministrator"]}>
              <ClassActionEvent />
            </ProtectedRoute>
          }
        >
          <Route path="details" element={<ClassActionEventDetailsTab />} />
          <Route path="materials" element={<ClassActionEventMaterialsTab />} />
          <Route path="stats" element={<ClassActionEventStatsTab />} />
        </Route>
        {/* NOBO Events - accessible by solicitor only */}
        <Route
          path="nobo-events"
          element={
            <ProtectedRoute allowedRoles={["solicitor"]}>
              <NoboEvents />
            </ProtectedRoute>
          }
        />
        {/* Reports - accessible by solicitor only */}
        <Route
          path="reports"
          element={
            <ProtectedRoute allowedRoles={["solicitor"]}>
              <Reports />
            </ProtectedRoute>
          }
        >
          <Route
            path="share-range-analysis-events"
            element={<ShareRangeAnalysisEvents />}
          />
          <Route
            path="location-analysis-events"
            element={<LocationAnalysisEvents />}
          />
        </Route>
        <Route
          path="share-range-analysis-events/:id"
          element={
            <ProtectedRoute allowedRoles={["solicitor"]}>
              <ShareRangeAnalysisEvent />
            </ProtectedRoute>
          }
        >
          <Route
            path="details"
            element={<ShareRangeAnalysisEventDetailsTab />}
          />
          <Route
            path="reports"
            element={<ShareRangeAnalysisEventReportsTab />}
          />
          <Route path="stats" element={<ShareRangeAnalysisEventStatsTab />} />
        </Route>
        <Route
          path="location-analysis-events/:id"
          element={
            <ProtectedRoute allowedRoles={["solicitor"]}>
              <LocationAnalysisEvent />
            </ProtectedRoute>
          }
        >
          <Route path="details" element={<LocationAnalysisEventDetailsTab />} />
          <Route path="reports" element={<LocationAnalysisEventReportsTab />} />
          <Route path="stats" element={<LocationAnalysisEventStatsTab />} />
        </Route>
        <Route
          path="nobo-events/:id"
          element={
            <ProtectedRoute allowedRoles={["solicitor"]}>
              <NoboEvent />
            </ProtectedRoute>
          }
        >
          <Route path="details" element={<NoboEventDetailsTab />} />
          <Route path="stats" element={<NoboEventStatsTab />} />
        </Route>
        {/* Page Not Found */}
        <Route path="not-found" element={<PageNotFound />} />
        <Route path="*" element={<PageNotFound />} />
        <Route path="issuers/:id" element={<Issuer />}>
          <Route path="proxy-events" element={<IssuerProxyEvents />} />
          <Route
            path="additional-mailings"
            element={<IssuerAdditionalMailings />}
          />
          <Route
            path="bankruptcy-events"
            element={<IssuerBankruptcyEvents />}
          />
          <Route
            path="class-action-events"
            element={<IssuerClassActionEvents />}
          />
          <Route path="nobo-events" element={<IssuerNoboEvents />} />
          <Route path="reports" element={<IssuerReports />}>
            <Route
              path="share-range-analysis-events"
              element={<IssuerShareRangeAnalysisEvents />}
            />
            <Route
              path="location-analysis-events"
              element={<IssuerLocationAnalysisEvents />}
            />
          </Route>
        </Route>
        <Route path="voting-instructions" element={<VotingInstruction />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}
